import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Frame from '../../components/uiParts/Frame';
import TitleBar from '../../components/uiParts/TitleBar';
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid';
import { jaJP } from '@mui/x-data-grid';
import BidSearchForm from '../../components/uiParts/bid/BidSearchForm';
import { useAuth } from '../../contexts/AuthContext';
import { usePost } from './../../apis/ApiRequests';
import { dateToString } from '../../utils/Library';
import CustomSnackBar from '../../components/alert/CustomSnackBar';

const BidHistory = () => {

	//===============================================
	// initialize
	//===============================================
    //-----------------------------------------------
    // initialize - 基本情報を設定
    //-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId;
    const luserName      = authInfo.userName;
    const authority      = authInfo.authority;
    const traderCode     = authInfo.traderCode;
    const trader         = authInfo.trader;
    const today          = authInfo.targetDate;
    const navigate       = useNavigate();
    
    //-----------------------------------------------
    // initialize - useState
    //-----------------------------------------------
    const [ entryDate,      setEntryDate      ] = useState(today);
    const [ condition ,     setCondition      ] = useState('');
    const [ searchStatus ,  setSearchStatus   ] = useState(1);
    const [ searchCount,    setSearchCount    ] = useState(0);
    const [ dataGridHeight, setDataGridHeight ] = useState(window.innerHeight - 210);
    const [ prevBidDate_1 , setPrevBidDate_1  ] = useState('');
    const [ prevBidDate_2 , setPrevBidDate_2  ] = useState('');
    const [ dataInputList,  setDataInputList  ] = useState([]);
    const [ requireMessage, setRequireMessage ] = useState('');
    const [ openRequire,    setOpenRequire    ] = useState(false);
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - bidGroupの検索
	//-----------------------------------------------
    const { response : searchData_res, loading : searchData_Loading, post:searchData_post } = usePost();
    const searchData = async () => {
        const numericDate = dateToString(entryDate);
        const postData = {
            'className'    : 'mch.models.table.BidGroup',
            'methodName'   : 'searchDataByDelivery',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'keyword1'     : numericDate,
            'keyword2'     : condition,
            'keyword3'     : searchStatus,
            'keyword4'     : traderCode,
            'keyword5'     : userId
        }
        await searchData_post(postData);
    };
	//-----------------------------------------------
    // API - 過去入札日の参照
	//-----------------------------------------------
    const { response : getPrevBidDate_res, post:getPrevBidDate_post } = usePost();
    const getPrevBidDate = async (uniqueId) => {
        const postData = {
            'className'    : 'mch.models.table.BidList',
            'methodName'   : 'getPrevBidDate',
            'facilityCode' : facilityCode,
            'userId'       : userId,
        }
        await getPrevBidDate_post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        getPrevBidDate();
        setSearchCount(searchCount+1);
        
        const handleResize = () => {
            setDataGridHeight(window.innerHeight - 210);
        }
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
        
    },[]);
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        searchData();
    }, [searchCount]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(searchDataByDataInput / bidGroup)
	//-----------------------------------------------
    useEffect(() => {
        setDataInputList([]);
        if (searchData_res) {
            setDataInputList(searchData_res.Data);
        }
    }, [searchData_res]);
	//-----------------------------------------------
    // useEffect - APIレスポンス(getPrevBidDate / bidList)
	//-----------------------------------------------
    useEffect(() => {
        setPrevBidDate_1('');
        setPrevBidDate_2('');
        if (getPrevBidDate_res) {
            setPrevBidDate_1(getPrevBidDate_res.prevDate1);
            setPrevBidDate_2(getPrevBidDate_res.prevDate2);
        }
    }, [getPrevBidDate_res]);

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
        setEntryDate(value);
        setSearchCount(searchCount+1);
    };
	//-----------------------------------------------
    // eventHandler - 検索ボタン
	//-----------------------------------------------
    const handleClick_search = () => {  
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            setSearchCount(searchCount+1);
        } else {
            handleClickSnackBarRequire();
        }
    };
	//-----------------------------------------------
    // eventHandler - 検索クリアボタン
	//-----------------------------------------------
    const handleClick_search_clear = () => {
        setEntryDate(today);
        setCondition('');
        setSearchStatus(1);
        setSearchCount(searchCount+1);
    };
	//===============================================
	// dataGrid
	//===============================================
	//-----------------------------------------------
    // dataGrid - リストの見出し
	//-----------------------------------------------
    const columnsxBidGroup = [
        { field: 'entryNo', headerName: '入札No', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, },
        { field: 'seq', headerName: '連番', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, },
        { field: 'bidName', headerName: '品名', width: 410, columnSeparatorWidth: 1, sortable:false, },
        { field: 'danbe', headerName: '水槽No', width: 90, columnSeparatorWidth: 1, sortable:false, },
        { field: 'trader', headerName: '仲買人', width: 120, columnSeparatorWidth: 1, sortable:false, },
        { field: 'tanka', headerName: '単価', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, editable:false},
        { field: 'bidSuryo', headerName: '数量', width: 80, columnSeparatorWidth: 1, type:'number', sortable:false, editable:false},
        {
            field: 'kgKubun',
            headerName: '単位',
            width: 80, sortable:false,
            renderCell: (params) => (
              <div>
                {unitMapping[params.value] || params.value}
              </div>
            ),
        },
    ];
    const unitMapping = {
        1: 'Kg',
        0: '箱',
    };
	//-----------------------------------------------
	// Function - 必須項目をチェックする
	//-----------------------------------------------
    const checkedRequire = () => {
        const errorFields = [];
        
        const numericEntryDate = dateToString(entryDate);
        
        if (authority == 3 && (numericEntryDate < prevBidDate_1)) errorFields.push('参照可能な入札日');
        if (errorFields.length) {
            const text = '過去の入札情報の参照は前回分まで可能です';
            setRequireMessage(text);
            return false;  // エラーがある場合は false を返します
        }
        
        return true;  // エラーがない場合は true を返します
    };
	//===============================================
	// UI Message
	//===============================================
    // スナックバー(必須チェック)を表示
    const handleClickSnackBarRequire = () => { setOpenRequire(true) };
    
	// スナックバー(必須チェック)を閉じる
    const handleCloseSnackBarRequire = (event, reason) => {
        if (reason === 'clickaway') { return } setOpenRequire(false);
    };
    //===============================================
    // Css
    //===============================================
    
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/home" setEntryDate={handleChange_entryDate} />
            <div className='bidHistory bodyContens'>
                <TitleBar title="入札履歴" />
                <div className='mainContents'>
                    <Frame classname="frame" title="入札情報検索">
                        <div style={{display:'flex'}}>
                            <div>
                                <BidSearchForm
                                    targetDateId       = "bidHistory_targetDate"
                                    targetDateValue    = { entryDate }
                                    targetDateOnChange = { (e) => setEntryDate(e.target.value) }
                                    keywordId          = "bidHistory_condition"
                                    keywordValue       = { condition }
                                    keywordOnChange    = { (e) => setCondition(e.target.value) }
                                    handleSearch       = { () => handleClick_search() }
                                    handleSearchClear  = { () => handleClick_search_clear() }
                                />
                            </div>
                        </div>
                        <div style={{marginTop:'10px'}}>
                            <CustomDataGrid
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                                editMode                = "row"
                                disableSelectionOnClick = { true } 
                                rows                    = { dataInputList }
                                columns                 = { columnsxBidGroup }
                                sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                                density                 = "compact"
                                localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                                showColumnRightBorder
                                showCellRightBorder
                                hideFooter
                                disableRowSelectionOnClick = {true}
                            />
                        </div>
                    </Frame>
                </div>
                <CustomSnackBar
                    open            = {openRequire}
                    onClose         = {() => handleCloseSnackBarRequire()}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {requireMessage}
                />
            </div>
        </>
    )
}
export default BidHistory;